import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { parseFormDefinition } from "@njbsoft/form-renderer";
import { SubmissionComposer } from "@njbsoft/form-composer";
import { ViewportBottomOwner } from "@/components/ui/viewport-bottom-owner";
import { type components } from "@njbsoft/types";
import { useWorkAssignment, isAssignmentComposable } from "@/hooks/use-work-assignments";
import { useChangeRequestSet } from "@/hooks/use-change-requests";
import { useFormSubmission, useFormSubmissions } from "@/hooks/use-form-submissions";
import { useTemplateVersion } from "@/hooks/use-form-template";
import { usePermissions } from "@/hooks/use-permissions";
import { useFacility } from "@/hooks/use-facilities";
import { useRecentPageTitle } from "@/hooks/use-recent-page-title";
import { useComposerCompletion } from "@/hooks/use-composer-completion";
import { useActiveOrgTimezone } from "@/hooks/use-active-org-timezone";
import { useToast } from "@/hooks/use-toast";
import { useAppStore } from "@/store";
import { isInspectionKind } from "@/lib/inspection-kinds";
import { ContextBar } from "@/components/test-entry/context-bar";
import { InspectionRail } from "@/components/test-entry/inspection-rail";
import { returnedFoundDevices } from "@/components/test-entry/returned-found-devices";
import { SlotDisabledBridge } from "@/components/test-entry/slot-disabled-bridge";
import { useFoundDevices } from "@/components/test-entry/use-found-devices";
import { useViolationProposalsHost } from "@/components/test-entry/use-violation-proposals-host";
import { useWebComposerPorts } from "@/components/test-entry/web-composer-ports";
import { returnedViolationProposals } from "@/components/violations/returned-violation-proposals";
import { useViolationProposals } from "@/components/violations/use-violation-proposals";
import { renderFacilityRefSlot } from "@/components/form-renderer/facility-ref-slot";
import { Card, CardContent, PageHeader, PageSkeleton } from "@/components/ui";

type Facility = components["schemas"]["Facility"];

interface TestEntryComposerSearch {
  /**
   * Incident-resolution seam (set by the submission-detail "Resolve incident"
   * CTA): the superseding submission MUST ride the predecessor's OWN
   * assignment chain — the backend supersede gate 422s a mismatched
   * assignment — so the CTA lands here, on the predecessor's assignment, and
   * threads its id through to the composer. When present, the composer mounts
   * even though the assignment is `completed` by resolve time (the backend
   * accepts new submissions on a completed assignment; the walk-up flow, which
   * binds a device's open test row or mints a manual row due today, never
   * rides a superseded submission's assignment and so can never satisfy the
   * same-chain gate).
   */
  supersedes_submission_id?: string;
}

// uuid-ish gate: canonical 8-4-4-4-12 hex (covers the v4 + v7 ids this codebase
// mints). A garbage / non-uuid id drops to undefined so the route simply mounts
// the composer without a supersede seam instead of threading a doomed id.
const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

function parseUuid(v: unknown): string | undefined {
  return typeof v === "string" && UUID_RE.test(v) ? v : undefined;
}

export const Route = createFileRoute("/_authenticated/test-entry/$assignmentId/")({
  component: TestEntryComposerPage,
  validateSearch: (s: Record<string, unknown>): TestEntryComposerSearch => ({
    supersedes_submission_id: parseUuid(s.supersedes_submission_id),
  }),
});

/**
 * Single-line "street, city, ST zip" for the oneoff-mode header subtitle
 * (mockup s4: "1420 Riverside Dr, Springfield, IL 62704"). Returns
 * `undefined` when every constituent field is empty so the caller can fall
 * back to the generic subtitle copy instead of rendering a blank line.
 */
function formatFacilityAddressLine(facility: Facility): string | undefined {
  const cityStateZip = [
    facility.city,
    [facility.state_code, facility.zip_code].filter(Boolean).join(" "),
  ]
    .filter(Boolean)
    .join(", ");
  const parts = [facility.address_line1, cityStateZip].filter((p) => p && p.trim() !== "");
  return parts.length > 0 ? parts.join(", ") : undefined;
}

/**
 * Route-guard banner for direct navigation to a terminal or already-submitted
 * assignment (`submitted` / `completed` / `cancelled`) -- mirrors the
 * lifecycle-terminal / template-builder guard-banner pattern elsewhere in the
 * app (a Card with an explanatory message + a back-to-source Link, in place
 * of the mounted composer).
 */
function GuardBanner({ assignmentId, message }: { assignmentId: string; message: string }) {
  const { t } = useTranslation("test-entry");
  return (
    <Card>
      <CardContent>
        <p className="text-sm text-on-surface">{message}</p>
        <div className="mt-4">
          <Link
            to="/test-activity/assignments/$id"
            params={{ id: assignmentId }}
            className="text-sm font-medium text-primary hover:underline"
          >
            {t("route.guard.backToAssignment")}
          </Link>
        </div>
      </CardContent>
    </Card>
  );
}

/**
 * The one-off composer entry point (mockup §4): loads the assignment + its
 * pinned template version, then mounts `SubmissionComposer` in
 * `mode="oneoff"` against the assignment's fixed target -- the locked
 * context-bar variant, with no facility/device carry-over editing. The
 * "Complete test" CTA on the assignment-detail page is the primary way in;
 * this route also guards direct navigation with the SAME two checks the CTA
 * itself is gated on (`can("form_submissions", "create")` -- deny-all while
 * unresolved, mirroring `/test-entry/new`'s `NoPermissionGuard` -- AND the
 * composable-status check): permission is ALSO enforced server-side on the
 * actual submit (no data-exposure hole either way), but this client gate
 * closes the same bookmark/URL-typing bypass `/new` closes, and a stale
 * bookmark to an already-submitted or cancelled assignment must not mount a
 * form with nowhere to go.
 *
 * The one exception is incident-resolution mode (a `supersedes_submission_id`
 * search param): the "Resolve incident" CTA routes here on the predecessor's
 * OWN assignment so the superseding submission rides the same assignment chain
 * the backend supersede gate requires, and that assignment is `completed` by
 * resolve time. The composable-status guard is bypassed while the supersede
 * param is present (the backend accepts new submissions on a completed
 * assignment when superseding); the permission guard still applies.
 *
 * An inspection (`isInspectionKind`) mounts two cards in the composer's rail
 * (`InspectionRail`): "Devices found", its entries riding the submit as
 * `change_requests`, and under it "Violations found", its entries riding as
 * `violation_proposals`. Without the search param, the route first reads the
 * assignment's newest submission; when that one was rejected, this entry
 * resubmits it by id, and reads its detail — the violation proposals it
 * carried, and the change-request set that rode it — before the composer
 * mounts: each card opens on what the rejection returned, editable, under one
 * line saying where they came from, and posts its entries as they stand at the
 * press — every one removed, the empty list, which says the redo proposes
 * none. A card whose read failed opens empty under a line saying so, and sends
 * no member until it holds an entry, so the server keeps what was returned. A
 * failed newest-submission read mounts a fresh entry with both cards.
 *
 * The Violations found card dates a new entry from the composer's live
 * context (the performed day its survey date names, else today on the org's
 * calendar). When the create refuses one of the violations it sent, the port
 * names the entry by its client id and the card marks it until that entry is
 * saved or removed, the next press, or a submit lands.
 */
function TestEntryComposerPage() {
  const { t } = useTranslation(["test-entry", "work-assignments"]);
  const toasts = useToast();
  const { assignmentId } = Route.useParams();
  const { supersedes_submission_id: supersedesSubmissionId } = Route.useSearch();
  const { can } = usePermissions();
  const orgId = useAppStore((s) => s.activeOrgId);
  const complete = useComposerCompletion();

  // Incident-resolution mode: the "Resolve incident" CTA lands here on the
  // predecessor's OWN (completed) assignment so the superseding submission
  // shares the same assignment chain the backend supersede gate requires. A
  // completed assignment is NOT composable in the normal one-off flow, but the
  // backend accepts new submissions against it when superseding — so the
  // composable guard is bypassed while this seam is active (no NEW status
  // block; the standard "Complete test" flow keeps the guard).
  const isResolving = supersedesSubmissionId !== undefined;

  const {
    data: assignment,
    isLoading: assignmentLoading,
    isError: assignmentError,
  } = useWorkAssignment(assignmentId);
  // The profile the composer's draft is kept under (the ports read the same
  // id); the found devices and the violations are kept beside that draft.
  const userId = useAppStore((s) => s.user?.id ?? null);
  const timezone = useActiveOrgTimezone() ?? null;
  const versionQuery = useTemplateVersion(assignment?.template_id, assignment?.template_version_id);
  // The composer's context-bar `disabled`, republished for the cards (see SlotDisabledBridge).
  const [slotDisabled, setSlotDisabled] = useState(false);

  const inspection = assignment !== undefined && isInspectionKind(assignment.target_kind);
  const readNewest = inspection && !isResolving;
  const newestQuery = useFormSubmissions(
    { assignment_id: assignmentId, sort_by: "received_at", sort_dir: "desc", limit: 1 },
    { enabled: readNewest },
  );
  // The newest submission's answer is taken once per assignment, from a read
  // that has settled fresh — never a cached page still refetching, nor another
  // assignment's held-over one — and then kept: the refetch a submit triggers
  // must not swap the composer's predecessor under it.
  const [resubmission, setResubmission] = useState<{
    assignmentId: string;
    rejectedPredecessorId: string | undefined;
  } | null>(null);
  if (
    readNewest &&
    resubmission?.assignmentId !== assignmentId &&
    !newestQuery.isFetching &&
    (newestQuery.isError || (newestQuery.isSuccess && !newestQuery.isPlaceholderData))
  ) {
    const newest = newestQuery.isSuccess ? newestQuery.data.items[0] : undefined;
    setResubmission({
      assignmentId,
      rejectedPredecessorId: newest?.status === "rejected" ? newest.id : undefined,
    });
  }
  const taken = resubmission?.assignmentId === assignmentId ? resubmission : null;
  const newestSettled = !readNewest || taken !== null;
  const rejectedPredecessorId = readNewest ? taken?.rejectedPredecessorId : undefined;
  const predecessorId = supersedesSubmissionId ?? rejectedPredecessorId;
  // Only the redo of the latched predecessor may send an emptied card as the
  // empty set; a mount whose newest read failed names none.
  const devices = useFoundDevices(
    assignmentId,
    assignment?.target_kind ?? "device_test",
    userId,
    rejectedPredecessorId,
  );
  const proposals = useViolationProposals(
    assignmentId,
    assignment?.target_kind ?? "device_test",
    userId,
    rejectedPredecessorId,
  );
  // A redo opens on the found devices the rejection returned: the rejected
  // submission's detail names the set that rode it, which holds them.
  const predecessorQuery = useFormSubmission(rejectedPredecessorId);
  const returnedSetId = predecessorQuery.data?.change_request_set_id ?? null;
  const returnedSetQuery = useChangeRequestSet(returnedSetId);
  let returnedRead: "none" | "pending" | "failed" | "read" = "none";
  // The proposals ride the detail itself: only its own read decides them.
  let proposalsRead: "none" | "pending" | "failed" | "read" = "none";
  if (rejectedPredecessorId !== undefined) {
    if (predecessorQuery.isError || returnedSetQuery.isError) returnedRead = "failed";
    else if (predecessorQuery.isSuccess && (returnedSetId === null || returnedSetQuery.isSuccess)) {
      returnedRead = "read";
    } else returnedRead = "pending";
    if (predecessorQuery.isError) proposalsRead = "failed";
    else proposalsRead = predecessorQuery.isSuccess ? "read" : "pending";
  }
  // The composer's live context (until its first publish, the assignee's with
  // no day) and the entry the create last refused, which the next press drops.
  const proposalsHost = useViolationProposalsHost(proposals, assignment?.assigned_to ?? null);
  // Every inspection's found devices and violations ride its submit, a redo's
  // included, as the hooks answer them: the entries; the empty list once a
  // redo's returned entries were all removed; otherwise no member.
  const ports = useWebComposerPorts(
    assignment?.device_installation_id ?? null,
    inspection ? devices.changeRequests : undefined,
    inspection ? proposalsHost.proposalsAtPress : undefined,
    inspection ? proposalsHost.onProposalRefused : undefined,
  );
  // Seeded once per rejected predecessor; a list this browser already seeded
  // for that redo is the inspector's work in progress and stays as it stands.
  // A failed read seeds no devices and pins nothing — a later read, a reload's
  // included, still seeds them — so the card sends no member until it holds
  // an entry and the server still carries what the rejection returned.
  const { seed } = devices;
  const returnedSet = returnedSetQuery.data;
  useEffect(() => {
    if (rejectedPredecessorId === undefined) return;
    if (returnedRead !== "read" && returnedRead !== "failed") return;
    seed(returnedRead === "read" ? returnedFoundDevices(returnedSet) : [], rejectedPredecessorId);
  }, [rejectedPredecessorId, returnedRead, returnedSet, seed]);
  // The violations the rejection returned, under the same rule.
  const { seed: seedProposals } = proposals;
  const predecessor = predecessorQuery.data;
  useEffect(() => {
    if (rejectedPredecessorId === undefined) return;
    if (proposalsRead !== "read" && proposalsRead !== "failed") return;
    seedProposals(
      proposalsRead === "read" ? returnedViolationProposals(predecessor) : [],
      rejectedPredecessorId,
    );
  }, [rejectedPredecessorId, proposalsRead, predecessor, seedProposals]);

  // Mockup s4: oneoff mode headlines the assignment's facility (name + a
  // single-line address subtitle) instead of the generic "Record test
  // results" title — the assignment fixes the target, so the header should
  // say WHERE, not restate the generic composer copy. The walk-up shell
  // never mounts this query at all, since its header stays the generic
  // rapid-entry title regardless of the carried-over facility chip — this
  // read and `formatFacilityAddressLine` live only here.
  const facilityQuery = useFacility(orgId, assignment?.facility_id ?? "");
  useRecentPageTitle(facilityQuery.data?.name ?? undefined);

  if (!can("form_submissions", "create")) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <PageHeader title={t("composer.title")} backTo="/test-activity" />
        <Card>
          <CardContent>
            <p className="text-sm text-on-surface">{t("route.guard.noPermission")}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (assignmentLoading) {
    return (
      <div className="p-4 sm:p-6">
        <PageSkeleton />
      </div>
    );
  }

  if (assignmentError || !assignment) {
    return (
      <div className="p-4 sm:p-6">
        <div className="rounded-md bg-danger/10 p-4 text-sm text-danger">
          {t("work-assignments:errors.loadDetailFailed")}
        </div>
      </div>
    );
  }

  if (!isResolving && !isAssignmentComposable(assignment.status)) {
    return (
      <div className="space-y-6 p-4 sm:p-6">
        <PageHeader title={t("route.title")} />
        <GuardBanner assignmentId={assignment.id} message={t("route.guard.notComposable")} />
      </div>
    );
  }

  if (versionQuery.isLoading || !newestSettled || returnedRead === "pending") {
    return (
      <div className="p-4 sm:p-6">
        <PageSkeleton />
      </div>
    );
  }

  if (versionQuery.isError || !versionQuery.data) {
    return (
      <div className="p-4 sm:p-6">
        <div className="rounded-md bg-danger/10 p-4 text-sm text-danger">
          {t("route.errorVersion")}
        </div>
      </div>
    );
  }

  const version = versionQuery.data;
  const templateTitle = assignment.template_title ?? "—";
  const templateLabel = `${templateTitle} · v${String(version.version)}`;

  const oneoffFacility = facilityQuery.data;
  const headerTitle = oneoffFacility?.name ?? t("composer.title");
  const headerSubtitle =
    (oneoffFacility && formatFacilityAddressLine(oneoffFacility)) ?? t("composer.subtitleOneoff");

  // `version.definition` is a DECODED object -- never a JSON string, so it
  // never gets run through a JSON-text parse. The Go publish-gate validator
  // is the schema authority for the wire document; the generated
  // `FormTemplateVersion.definition` type is an intentionally loose
  // `{ [key: string]: unknown }` record, which `parseFormDefinition` narrows.
  const definition = parseFormDefinition(version.definition);

  return (
    <ViewportBottomOwner>
      <div className="p-4 sm:p-6">
        <SubmissionComposer
          mode="oneoff"
          assignmentId={assignment.id}
          definition={definition}
          facilityId={assignment.facility_id}
          targetKind={assignment.target_kind}
          deviceInstallationId={assignment.device_installation_id ?? null}
          templateTitle={templateTitle}
          templateVersion={version.version}
          defaultPerformerId={assignment.assigned_to ?? null}
          supersedesSubmissionId={predecessorId}
          ports={ports}
          header={
            <PageHeader title={headerTitle} subtitle={headerSubtitle} backTo="/test-activity" />
          }
          contextBar={(p) => (
            <>
              <SlotDisabledBridge disabled={p.disabled} publish={setSlotDisabled} />
              <ContextBar {...p} templateLabel={templateLabel} />
            </>
          )}
          timezone={timezone}
          onLiveContextChange={proposalsHost.onLiveContextChange}
          // Below `lg` the rail collapses to a strip naming the premise the
          // header resolves; a device test mounts no rail column, so no strip.
          stripTarget={inspection ? oneoffFacility?.name : undefined}
          railTrailing={
            inspection ? (
              <InspectionRail
                devices={devices}
                proposals={proposals}
                facilityId={assignment.facility_id}
                liveContext={proposalsHost.liveContext}
                timezone={timezone}
                disabled={slotDisabled}
                refusal={proposalsHost.refusal}
                onEntryChanged={proposalsHost.onEntryChanged}
                redo={
                  rejectedPredecessorId === undefined
                    ? null
                    : {
                        devicesReadFailed: returnedRead === "failed",
                        proposalsReadFailed: proposalsRead === "failed",
                      }
                }
              />
            ) : undefined
          }
          facilityRefSlot={renderFacilityRefSlot}
          onSubmitted={(result) => {
            devices.clear();
            proposalsHost.clear();
            toasts.success("test-entry:composer.submittedToast");
            complete(result);
          }}
        />
      </div>
    </ViewportBottomOwner>
  );
}
